import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import CreditPurchase from '../CreditPurchase/CreditPurchase';
import './MiniatureVisualizer.css';

const STEPS = [
  'Analyzing miniature…',
  'Generating color scheme…',
  'Creating visualization…',
];

function ImageUploadZone({ label, required, image, onImage }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  function handleFile(file) {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) =>
      onImage({ base64: e.target.result.split(',')[1], mediaType: file.type, preview: e.target.result });
    reader.readAsDataURL(file);
  }

  return (
    <div
      className={`upload-zone ${dragging ? 'dragging' : ''} ${image ? 'has-image' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
      aria-label={`Upload ${label} image`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="upload-input"
        onChange={(e) => handleFile(e.target.files[0])}
      />
      {image ? (
        <div className="upload-preview">
          <img src={image.preview} alt={`${label} preview`} className="preview-img" />
          <button
            className="remove-img-btn"
            onClick={(e) => { e.stopPropagation(); onImage(null); }}
            aria-label="Remove image"
          >
            ×
          </button>
        </div>
      ) : (
        <div className="upload-placeholder">
          <div className="upload-icon" aria-hidden="true">📷</div>
          <p className="upload-label">
            {label} {required && <span className="required-star">*</span>}
          </p>
          <p className="upload-hint">Click or drag & drop</p>
        </div>
      )}
    </div>
  );
}

function MiniatureVisualizer() {
  const { isAuthenticated, isLoading: authLoading, signIn, getIdToken } = useAuth();
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [credits, setCredits] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);

  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
  // Function URL has no 29-second timeout unlike API Gateway
  const visualizeUrl = process.env.REACT_APP_VISUALIZE_URL || `${apiEndpoint}/visualize`;

  useEffect(() => {
    if (isAuthenticated) {
      fetchCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Check for payment success in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setTimeout(() => fetchCredits(), 2000);
      window.history.replaceState({}, '', '/visualizer');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchCredits() {
    try {
      const token = await getIdToken();
      const response = await fetch(`${apiEndpoint}/credits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setCredits(data.credits ?? 0);
    } catch {
      setCredits(0);
    }
  }

  async function handleGenerate() {
    if (!frontImage || credits < 1) return;
    setIsGenerating(true);
    setError('');
    setResult(null);
    setStepIndex(0);

    const stepTimer = setInterval(() => {
      setStepIndex((prev) => Math.min(prev + 1, STEPS.length - 1));
    }, 8000);

    try {
      const token = await getIdToken();
      const response = await fetch(visualizeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          frontImageBase64: frontImage.base64,
          frontImageMediaType: frontImage.mediaType,
          backImageBase64: backImage?.base64 || null,
          backImageMediaType: backImage?.mediaType || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 402) {
          setShowCreditPurchase(true);
          throw new Error('You need more credits to generate a visualization.');
        }
        throw new Error(data.error || 'Generation failed');
      }

      setResult(data);
      setCredits(data.creditsRemaining);
    } catch (err) {
      setError(err.message);
    } finally {
      clearInterval(stepTimer);
      setIsGenerating(false);
    }
  }

  function downloadImage() {
    if (!result?.imageBase64) return;
    const link = document.createElement('a');
    link.href = `data:image/jpeg;base64,${result.imageBase64}`;
    link.download = 'miniature-visualization.jpg';
    link.click();
  }

  if (authLoading) {
    return (
      <div className="visualizer-loading-state">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="visualizer-signin-prompt">
        <div className="signin-card glass-card">
          <div className="signin-icon" aria-hidden="true">🎨</div>
          <h2 className="signin-title">AI Miniature Visualizer</h2>
          <p className="signin-description">
            Upload photos of your unpainted miniature and let AI suggest a color scheme
            and generate a painted visualization — powered by Claude and Stable Diffusion.
          </p>
          <ul className="signin-features">
            <li>📸 Upload front & back photos</li>
            <li>🤖 AI-powered color scheme generation</li>
            <li>🖼️ Photorealistic painted visualization</li>
            <li>💾 Download your result</li>
          </ul>
          <button className="btn-primary signin-google-btn" onClick={signIn}>
            <svg className="google-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
          <p className="signin-note">Free to try — sign in to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="visualizer-content">
      {/* Credit bar */}
      <div className="credits-bar">
        <span className="credits-info">
          <span className="credits-icon" aria-hidden="true">⚡</span>
          <span className="credits-count">
            {credits === null ? '…' : credits} credit{credits !== 1 ? 's' : ''} remaining
          </span>
        </span>
        <button
          className="btn-buy-credits"
          onClick={() => setShowCreditPurchase(true)}
        >
          Buy Credits
        </button>
      </div>

      {/* Upload section */}
      <div className="upload-section glass-card">
        <h2 className="section-title">Upload Your Miniature</h2>
        <p className="section-subtitle">
          Upload a front photo (required) and optional back photo for better results
        </p>
        <div className="upload-grid">
          <ImageUploadZone
            label="Front View"
            required
            image={frontImage}
            onImage={setFrontImage}
          />
          <ImageUploadZone
            label="Back View"
            required={false}
            image={backImage}
            onImage={setBackImage}
          />
        </div>

        {error && (
          <div className="visualizer-error" role="alert">
            {error}
          </div>
        )}

        <button
          className="btn-generate"
          onClick={handleGenerate}
          disabled={!frontImage || credits < 1 || isGenerating}
        >
          {isGenerating ? (
            <span className="generate-loading">
              <span className="spinner-sm" />
              {STEPS[stepIndex]}
            </span>
          ) : (
            <>
              <span aria-hidden="true">✨</span>
              Generate Visualization
              <span className="credit-cost">(1 credit)</span>
            </>
          )}
        </button>

        {credits === 0 && !isGenerating && (
          <p className="no-credits-msg">
            You have no credits.{' '}
            <button
              className="link-btn"
              onClick={() => setShowCreditPurchase(true)}
            >
              Purchase credits
            </button>{' '}
            to generate visualizations.
          </p>
        )}
      </div>

      {/* Results section */}
      {result && (
        <div className="results-section glass-card">
          <h2 className="section-title">Your Visualization</h2>
          <div className="results-grid">
            <div className="result-image-container">
              <img
                src={`data:image/jpeg;base64,${result.imageBase64}`}
                alt="AI generated painted miniature visualization"
                className="result-image"
              />
              <button className="btn-download" onClick={downloadImage}>
                <span aria-hidden="true">⬇</span> Download Image
              </button>
            </div>
            <div className="result-details">
              <div className="result-block">
                <h3 className="result-block-title">Color Scheme</h3>
                <p className="result-text">{result.colorScheme}</p>
              </div>
              <div className="result-block">
                <h3 className="result-block-title">Miniature Description</h3>
                <p className="result-text muted">{result.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreditPurchase && (
        <CreditPurchase onClose={() => setShowCreditPurchase(false)} />
      )}
    </div>
  );
}

export default MiniatureVisualizer;
